import React from "react";
import { FaPlus, FaPrint, FaUpload } from "react-icons/fa";
import { PrimaryButton, SecondaryButton } from "../../UI/DesignStandards";
import CSVExportButton from "./CSVExportButton";

const compactButtonClass = "flex items-center gap-2 !px-3 !py-2 text-sm";

const ActionGroup = ({ label, children }) => (
  <div className="flex flex-wrap items-center gap-2">
    <span className="text-xs font-display font-bold uppercase tracking-wider text-brand-muted">
      {label}
    </span>
    {children}
  </div>
);

const ActionButtons = ({
  data,
  onCreateTeam,
  onPrintSheets,
  onPrintRecaps,
  onUploadTeams,
  onUploadScores,
  canManage = false,
  canImportSheets = false,
  isPayoutFinalized = false,
  showCreate = true,
}) => {
  const hasSquads = data?.squads && data.squads.length > 0;
  const hasEvents = data?.events && data.events.length > 0;
  const canCreateTeam = hasSquads && hasEvents;
  const createDisabled = isPayoutFinalized || !canCreateTeam;
  const scoresDisabled = isPayoutFinalized;
  const printDisabled = !hasEvents;
  const recapsDisabled = !hasSquads;

  const createTitle = () => {
    if (isPayoutFinalized) {
      return "Payouts have been locked and cannot be changed";
    }
    if (!hasSquads) {
      return "Create squads before creating teams";
    }
    if (!hasEvents) {
      return "Create events before creating teams";
    }
    return "Create a new team";
  };

  const scoresTitle = () => {
    if (isPayoutFinalized) {
      return "Payouts have been locked and cannot be changed";
    }
    return "Import files to enter scores from recaps";
  };

  const paperTools = canManage ? (
    <>
      <ActionGroup label="Signups">
        <SecondaryButton
          onClick={onPrintSheets}
          icon={FaPrint}
          className={compactButtonClass}
          disabled={printDisabled}
          aria-label="Print signups"
          title={
            printDisabled
              ? "Create events before printing signups"
              : "Print blank signup sheets"
          }
        >
          Print
        </SecondaryButton>
        {canImportSheets ? (
          <SecondaryButton
            onClick={onUploadTeams}
            icon={FaUpload}
            className={compactButtonClass}
            disabled={createDisabled}
            aria-label="Import signups"
            title={
              createDisabled
                ? createTitle()
                : "Import signup sheets to create teams"
            }
          >
            Import
          </SecondaryButton>
        ) : null}
      </ActionGroup>
      <ActionGroup label="Recaps">
        <SecondaryButton
          onClick={onPrintRecaps}
          icon={FaPrint}
          className={compactButtonClass}
          disabled={recapsDisabled}
          aria-label="Print recaps"
          title={
            recapsDisabled
              ? "Create squads before printing recaps"
              : "Print recaps for assigned pairs"
          }
        >
          Print
        </SecondaryButton>
        {canImportSheets ? (
          <SecondaryButton
            onClick={onUploadScores}
            icon={FaUpload}
            className={compactButtonClass}
            disabled={scoresDisabled}
            aria-label="Import recaps"
            title={scoresTitle()}
          >
            Import
          </SecondaryButton>
        ) : null}
      </ActionGroup>
    </>
  ) : null;

  const createButton =
    canManage && showCreate ? (
      <PrimaryButton
        onClick={onCreateTeam}
        icon={FaPlus}
        className="flex items-center gap-2"
        disabled={createDisabled}
        title={createTitle()}
      >
        Create Team
      </PrimaryButton>
    ) : null;

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      {paperTools}
      <div className="flex flex-wrap items-center gap-2 ml-auto">
        {createButton}
        <CSVExportButton data={data} />
      </div>
    </div>
  );
};

export default ActionButtons;
